import {FeatureFlagEnum} from 'src/api/ProactApiClient';
import {createDomainSession} from 'src/offline/domainSession';
import type {EnsureDomainSessionOptions} from 'src/offline/domainSession';
import {store} from 'src/redux/store';
import {getOfflineBundle} from './api';
import {
  buildTasksHydrationPlan,
  cacheTaskInlineAttachments,
  estimateBundleBytes,
} from './tasksBundle';
import {tasksAssignmentsListKey, TASKS_CONTEXT_KEY} from './snapshotKeys';

/** Assignments cached longer than this are refreshed by the scheduler. */
export const TASKS_CACHE_MAX_AGE_MS = 6 * 60 * 60 * 1000;

const TASK_FEATURES = [
  FeatureFlagEnum.Actions,
  FeatureFlagEnum.ToolboxTalks,
  FeatureFlagEnum.Approvals,
];

const hasTaskFeature = (): boolean => {
  const active = store.getState().session.instance?.activeFeatures ?? [];
  return TASK_FEATURES.some(feature => active.includes(feature));
};

export const tasksSession = createDomainSession({
  contextKey: TASKS_CONTEXT_KEY,
  isEnabled: hasTaskFeature,
  fetchBundle: getOfflineBundle,
  buildPlan: buildTasksHydrationPlan,
  estimateBytes: estimateBundleBytes,
  afterHydrate: cacheTaskInlineAttachments,
  staleKey: tasksAssignmentsListKey,
  maxAgeMs: TASKS_CACHE_MAX_AGE_MS,
  storageAlertText:
    'Not enough storage to save your tasks for offline use. Free up space on this device to complete tasks offline.',
  offlineAlertText: 'You are offline. Reconnect to refresh offline tasks.',
});

export const ensureTasksOfflineSession = (
  options: EnsureDomainSessionOptions = {}
): Promise<void> => tasksSession.ensure(options);

export const useTasksOfflineAutoSession = tasksSession.useAutoSession;

export const startTasksCacheScheduler = (): (() => void) =>
  tasksSession.startScheduler();
